import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import {
  RedoTable,
  type RedoTableColumn,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import type { PartnerSummary } from "@redotech/admin-sdk/rpc/schema/partners/shared";

import { RedoAdminRpcClientContext } from "../app/redo-admin-rpc-client-provider";
import * as listStyles from "../onboardings/returns/returns-page.module.css";
import * as paginationStyles from "../onboardings/v2/v2-page.module.css";
import {
  PartnersListingToolbar,
  type StatusFilter,
} from "./components/partners-listing-toolbar";
import { PARTNERS_COLUMNS } from "./components/partners-table";
import { CreatePartnerModal } from "./create-partner-modal";

const SEARCH_DEBOUNCE_MS = 300;
const ALLOWED_PAGE_SIZES = [10, 25, 50, 100] as const;
type PageSize = (typeof ALLOWED_PAGE_SIZES)[number];
const DEFAULT_PAGE_SIZE: PageSize = 25;
const PAGE_SIZE_OPTIONS = ALLOWED_PAGE_SIZES.map((size) => ({
  id: String(size),
  value: size,
  type: "text" as const,
  text: `${size} / page`,
}));

export const PartnersPage = memo(function PartnersPage() {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Hydrate filter state from URL — single source of truth per decision 2A.
  const offset = useMemo(() => {
    const raw = Number(searchParams.get("offset") ?? "0");
    return Number.isFinite(raw) && raw >= 0 ? Math.floor(raw) : 0;
  }, [searchParams]);

  const pageSize = useMemo<PageSize>(() => {
    const raw = Number(searchParams.get("size") ?? String(DEFAULT_PAGE_SIZE));
    return (ALLOWED_PAGE_SIZES as readonly number[]).includes(raw)
      ? (raw as PageSize)
      : DEFAULT_PAGE_SIZE;
  }, [searchParams]);

  const searchFromUrl = searchParams.get("q") ?? "";
  const statusFromUrl = (searchParams.get("status") ?? "all") as StatusFilter;

  // Local controlled value for the search input. Debounced into URL.
  const [searchInput, setSearchInput] = useState(searchFromUrl);
  const [debouncedSearch, setDebouncedSearch] = useState(searchFromUrl);

  // Sync the local input with URL changes that come from elsewhere
  // (browser back / forward, programmatic navigation).
  useEffect(() => {
    setSearchInput(searchFromUrl);
    setDebouncedSearch(searchFromUrl);
  }, [searchFromUrl]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(searchInput),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Whenever debounced search settles, sync to URL (without keeping the
  // input local-only, so refresh preserves it).
  useEffect(() => {
    if (debouncedSearch === searchFromUrl) return;
    const next = new URLSearchParams(searchParams);
    if (debouncedSearch) {
      next.set("q", debouncedSearch);
    } else {
      next.delete("q");
    }
    next.delete("offset");
    setSearchParams(next, { replace: true });
  }, [debouncedSearch, searchFromUrl, searchParams, setSearchParams]);

  const setStatusFilter = useCallback(
    (status: StatusFilter) => {
      const next = new URLSearchParams(searchParams);
      if (status === "all") {
        next.delete("status");
      } else {
        next.set("status", status);
      }
      next.delete("offset");
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  const setOffset = useCallback(
    (newOffset: number) => {
      const next = new URLSearchParams(searchParams);
      if (newOffset > 0) {
        next.set("offset", String(newOffset));
      } else {
        next.delete("offset");
      }
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  const setPageSize = useCallback(
    (size: PageSize) => {
      const next = new URLSearchParams(searchParams);
      if (size === DEFAULT_PAGE_SIZE) {
        next.delete("size");
      } else {
        next.set("size", String(size));
      }
      // Resetting page size collapses back to the first page so we don't
      // strand the user past the new last page.
      next.delete("offset");
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchInput("");
    setDebouncedSearch("");
    const next = new URLSearchParams();
    setSearchParams(next, { replace: false });
  }, [setSearchParams]);

  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const dataLoad = useLoad(
    async (signal) => {
      return rpcClient.listPartners(
        {
          offset,
          limit: pageSize,
          searchQuery: debouncedSearch || undefined,
          statusFilter: statusFromUrl,
        },
        { signal },
      );
    },
    [rpcClient, offset, pageSize, debouncedSearch, statusFromUrl, refreshKey],
  );

  const [isModalOpen, setIsModalOpen] = useState(false);

  const hasFilters = debouncedSearch.length > 0 || statusFromUrl !== "all";

  return (
    <div className={listStyles.pageWrapper}>
      <div className={listStyles.pageContent}>
        <div className={listStyles.allMerchantsContent}>
          <PartnersListingToolbar
            onCreateClick={() => setIsModalOpen(true)}
            onSearchChange={setSearchInput}
            onStatusFilterChange={setStatusFilter}
            searchQuery={searchInput}
            statusFilter={statusFromUrl}
          />

          <div className={listStyles.tableCard}>
            {dataLoad.pending && dataLoad.value == null ? (
              <div className={listStyles.tableLoadingState}>
                Loading partners…
              </div>
            ) : dataLoad.error ? (
              <div className={listStyles.tableErrorState}>
                Failed to load partners.{" "}
                <RedoButton
                  hierarchy="link"
                  onClick={refresh}
                  size="xs"
                  text="Retry"
                />
              </div>
            ) : (dataLoad.value?.partners.length ?? 0) === 0 ? (
              hasFilters ? (
                <FilteredEmptyState onClearFilters={clearFilters} />
              ) : (
                <TrulyEmptyState onCreate={() => setIsModalOpen(true)} />
              )
            ) : (
              <>
                <RedoTable<PartnerSummary>
                  layout={{
                    columns:
                      PARTNERS_COLUMNS as RedoTableColumn<PartnerSummary>[],
                  }}
                  onRowClick={(row) => navigate(`/partners/${row.id}`)}
                  rowsOfData={dataLoad.value?.partners ?? []}
                />
                <PartnersPaginationFooter
                  isLoading={dataLoad.pending}
                  offset={offset}
                  onOffsetChange={setOffset}
                  onPageSizeChange={setPageSize}
                  pageSize={pageSize}
                  total={dataLoad.value?.total ?? 0}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <CreatePartnerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreated={() => refresh()}
      />
    </div>
  );
});

const TrulyEmptyState = memo(function TrulyEmptyState({
  onCreate,
}: {
  onCreate: () => void;
}) {
  return (
    <Flex align="center" dir="column" gap="md" justify="center" p="3xl">
      <Text fontSize="md">No partners yet.</Text>
      <RedoButton
        hierarchy="primary"
        onClick={onCreate}
        size="sm"
        text="Create partner"
      />
    </Flex>
  );
});

const FilteredEmptyState = memo(function FilteredEmptyState({
  onClearFilters,
}: {
  onClearFilters: () => void;
}) {
  return (
    <Flex align="center" dir="column" gap="md" justify="center" p="3xl">
      <Text fontSize="md">No partners match these filters.</Text>
      <RedoButton
        hierarchy="secondary"
        onClick={onClearFilters}
        size="sm"
        text="Clear filters"
      />
    </Flex>
  );
});

interface PaginationFooterProps {
  offset: number;
  pageSize: PageSize;
  total: number;
  isLoading: boolean;
  onOffsetChange: (offset: number) => void;
  onPageSizeChange: (size: PageSize) => void;
}

const PartnersPaginationFooter = memo(function PartnersPaginationFooter({
  offset,
  pageSize,
  total,
  isLoading,
  onOffsetChange,
  onPageSizeChange,
}: PaginationFooterProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(
    totalPages,
    Math.max(1, Math.floor(offset / pageSize) + 1),
  );
  const canPrev = currentPage > 1 && !isLoading;
  const canNext = currentPage < totalPages && !isLoading;
  const rangeStart = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(total, currentPage * pageSize);

  const goToPage = (page: number) => {
    onOffsetChange(Math.max(0, (page - 1) * pageSize));
  };

  const selectedPageSizeItem =
    PAGE_SIZE_OPTIONS.find((o) => o.value === pageSize) ??
    PAGE_SIZE_OPTIONS[1]!;

  return (
    <div className={paginationStyles.paginationFooter}>
      <div className={paginationStyles.paginationSummary}>
        <span className={paginationStyles.paginationSummaryText}>
          {total === 0
            ? "No partners"
            : `Showing ${rangeStart.toLocaleString()}–${rangeEnd.toLocaleString()} of ${total.toLocaleString()}`}
        </span>
      </div>
      <div className={paginationStyles.paginationControls}>
        <div className={paginationStyles.paginationPageSize}>
          <RedoSingleSelectDropdownInput<PageSize>
            options={PAGE_SIZE_OPTIONS}
            optionSelected={({ item }) => onPageSizeChange(item.value)}
            selectedItem={selectedPageSizeItem}
            size="xs"
          />
        </div>
        <div className={paginationStyles.paginationPager}>
          <button
            aria-label="First page"
            className={paginationStyles.paginationButton}
            disabled={!canPrev}
            onClick={() => goToPage(1)}
            type="button"
          >
            ‹‹
          </button>
          <button
            aria-label="Previous page"
            className={paginationStyles.paginationButton}
            disabled={!canPrev}
            onClick={() => goToPage(currentPage - 1)}
            type="button"
          >
            ‹ Prev
          </button>
          <span className={paginationStyles.paginationPageLabel}>
            Page {currentPage.toLocaleString()} of {totalPages.toLocaleString()}
          </span>
          <button
            aria-label="Next page"
            className={paginationStyles.paginationButton}
            disabled={!canNext}
            onClick={() => goToPage(currentPage + 1)}
            type="button"
          >
            Next ›
          </button>
          <button
            aria-label="Last page"
            className={paginationStyles.paginationButton}
            disabled={!canNext}
            onClick={() => goToPage(totalPages)}
            type="button"
          >
            ››
          </button>
        </div>
      </div>
    </div>
  );
});
